import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { fireConfetti, haptic } from "@/lib/celebrate";
import { popCredits } from "@/components/CreditPop";

export const WeeklyChallengeCard = () => {
  const { api, user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const fetchChallenge = async () => {
    try {
      const r = await api.get("/artists/me/weekly-challenge");
      setData(r.data);
    } catch {}
  };

  useEffect(() => {
    if (user?.role === "artist") fetchChallenge();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !data.available) return null;

  const pct = Math.min(100, Math.round((data.progress / data.target) * 100));
  const isComplete = data.completed;
  const isClaimed = data.claimed;

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const res = await api.post("/artists/me/weekly-challenge/claim");
      fireConfetti("big");
      haptic(80);
      popCredits(res.data.reward_credits, "credits");
      toast.success(`Defi reussi ! +${res.data.reward_credits} credits boost.`, { duration: 5000 });
      refreshUser();
      fetchChallenge();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setClaiming(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-3xl p-4 shadow-sm"
      data-testid="weekly-challenge-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
          isClaimed ? "bg-emerald-500/15" : isComplete ? "bg-gradient-to-br from-[#FF6B35] to-[#7000FF]" : "bg-[#FF6B35]/15"
        }`}>
          {isClaimed ? (
            <Check className="w-5 h-5 text-emerald-500" />
          ) : isComplete ? (
            <Sparkles className="w-5 h-5 text-white" />
          ) : (
            <Trophy className="w-5 h-5 text-[#FF6B35]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading font-bold text-sm text-card-foreground">{data.label}</p>
            <span className="text-[10px] font-mono-credits font-bold text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-0.5 rounded-full">
              +{data.reward_credits}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">{data.description}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-muted rounded-full h-2.5 overflow-hidden mb-2">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full rounded-full ${
            isClaimed ? "bg-emerald-500" : "bg-gradient-to-r from-[#FF6B35] to-[#7000FF]"
          }`}
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground font-mono-credits">
          {data.progress} / {data.target} contenus
        </p>
        {isClaimed ? (
          <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider" data-testid="weekly-claimed">
            Recompense recuperee
          </span>
        ) : isComplete ? (
          <button
            onClick={handleClaim}
            disabled={claiming}
            className="bg-gradient-to-r from-[#FF6B35] to-[#7000FF] text-white font-bold px-3 py-1.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
            data-testid="weekly-claim-btn"
          >
            {claiming ? "..." : "Reclamer +" + data.reward_credits}
          </button>
        ) : (
          <span className="text-[10px] text-muted-foreground">
            {data.target - data.progress} restant{data.target - data.progress > 1 ? "s" : ""}
          </span>
        )}
      </div>
    </motion.div>
  );
};
